import React, { useMemo, useState } from 'react';

import { Checkbox as BlueprintCheckbox } from '@buildoutinc/blueprint-react/ui/Checkbox';
import { Empty } from '@buildoutinc/blueprint-react/ui/Empty';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';
import { Label } from '@buildoutinc/blueprint-react/ui/Label';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { Checkbox } from 'components/ui/checkbox';
import PaginationBlueprint from 'components/ui/pagination_blueprint';

import OfficesField from './offices_field';

import type { OfficeOption } from './offices_field';
import type { OupData, OupUser } from 'components/users/types';

const PER_PAGE = 10;

interface Props {
  users: OupUser[];
  officesById: OupData['officesById'];
  officeOptions: OfficeOption[];
  selectedUserIds: number[];
  onChange: (ids: number[]) => void;
}

const fullName = (user: OupUser): string => `${user.firstName} ${user.lastName}`;

export default function OtherUsersTable({ users, officesById, officeOptions, selectedUserIds, onChange }: Props) {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [officeIds, setOfficeIds] = useState<number[]>([]);

  // Ordered once: re-sorting as boxes are ticked would move rows out from under the cursor.
  const [sortedUsers] = useState(() => [
    ...users.filter(user => selectedUserIds.includes(user.id)),
    ...users.filter(user => !selectedUserIds.includes(user.id)),
  ]);

  const visibleUsers = useMemo(() => {
    const term = query.trim().toLowerCase();

    return sortedUsers.filter(user => (
      (!term || fullName(user).toLowerCase().includes(term))
        && (officeIds.length === 0 || officeIds.includes(user.companyOfficeId))
    ));
  }, [sortedUsers, query, officeIds]);

  const totalPages = Math.ceil(visibleUsers.length / PER_PAGE) || 1;
  const pageUsers = visibleUsers.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const selectedOnPage = pageUsers.filter(user => selectedUserIds.includes(user.id)).length;

  const toggleUser = (id: number, checked: boolean) => onChange(
    checked ? [...selectedUserIds, id] : selectedUserIds.filter(selected => selected !== id),
  );

  const togglePage = (checked: boolean) => {
    const ids = pageUsers.map(user => user.id);

    onChange(checked
      ? [...new Set([...selectedUserIds, ...ids])]
      : selectedUserIds.filter(id => !ids.includes(id)));
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-start gap-3 mb-3">
        <div className="flex-grow-1">
          <Label htmlFor="other-users-search">Search</Label>
          <Input
            id="other-users-search"
            placeholder="Search by name"
            type="text"
            value={query}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setQuery(event.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="flex-grow-1">
          <Label htmlFor="other-users-office-filter">Office</Label>
          <OfficesField
            inputId="other-users-office-filter"
            options={officeOptions}
            placeholder="Filter by office"
            selectedIds={officeIds}
            onChange={(ids) => {
              setOfficeIds(ids);
              setPage(1);
            }}
          />
        </div>
      </div>

      <p className="fw-semibold mb-2" data-testid="selected-count">
        {selectedUserIds.length > 0 ? `Selected Users (${selectedUserIds.length})` : 'Selected Users'}
      </p>

      {pageUsers.length === 0 ? (
        <Empty>
          <Empty.Title>
            {users.length === 0 ? 'There are no other users at this company' : 'No users match those filters'}
          </Empty.Title>
        </Empty>
      ) : (
        <>
          <Table className="align-middle">
            <Table.Header>
              <Table.Row>
                <Table.Head>
                  <div className="d-flex align-items-center gap-2">
                    <BlueprintCheckbox
                      aria-label="Select every user on this page"
                      checked={selectedOnPage === pageUsers.length}
                      data-testid="select-page"
                      indeterminate={selectedOnPage > 0 && selectedOnPage < pageUsers.length}
                      onCheckedChange={checked => togglePage(checked === true)}
                    />
                    Name
                  </div>
                </Table.Head>
                <Table.Head>Office</Table.Head>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {pageUsers.map(user => (
                <Table.Row key={user.id}>
                  <Table.Cell>
                    <Checkbox
                      checked={selectedUserIds.includes(user.id)}
                      data-testid="other-user"
                      data-user-id={user.id}
                      label={fullName(user)}
                      onCheckedChange={checked => toggleUser(user.id, checked === true)}
                    />
                  </Table.Cell>
                  <Table.Cell className="text-muted">{officesById[user.companyOfficeId]?.name}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <PaginationBlueprint currentPage={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}
    </div>
  );
}
